// ============================================================================
// Whiteboard Types
// ============================================================================

export type ElementType =
  | 'button'
  | 'input'
  | 'dropdown'
  | 'checkbox'
  | 'radio_group'
  | 'toggle'
  | 'table'
  | 'text'
  | 'image'
  | 'shape_rect'
  | 'shape_circle'
  | 'container'
  | 'card'
  | 'modal'
  | 'tabs'
  | 'accordion'
  | 'list'
  | 'icon';

export type ElementState = 'default' | 'hover' | 'focus' | 'disabled' | 'error' | 'loading' | 'filled';

export type AnchorPosition = 'top' | 'right' | 'bottom' | 'left';

// ============================================================================
// Device Frames
// ============================================================================

export type DeviceType =
  | 'iphone_15'
  | 'iphone_se'
  | 'android'
  | 'ipad'
  | 'desktop'
  | 'desktop_wide'
  | 'custom';

export interface DeviceFrame {
  id: string;
  name: string;
  deviceType: DeviceType;
  x: number;
  y: number;
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
  backgroundColor: string;
  children: string[];
  scrollOffset: number;
  locked: boolean;
  zIndex: number;
}

export interface DeviceFramePreset {
  label: string;
  icon: string;
  viewportWidth: number;
  viewportHeight: number;
  chromeType: 'phone' | 'tablet' | 'desktop';
}

export const DEVICE_FRAME_PRESETS: Record<Exclude<DeviceType, 'custom'>, DeviceFramePreset> = {
  iphone_15: { label: 'iPhone 15 / 16', icon: 'smartphone', viewportWidth: 393, viewportHeight: 852, chromeType: 'phone' },
  iphone_se: { label: 'iPhone SE', icon: 'smartphone', viewportWidth: 375, viewportHeight: 667, chromeType: 'phone' },
  android: { label: 'Android (Pixel)', icon: 'smartphone', viewportWidth: 412, viewportHeight: 915, chromeType: 'phone' },
  ipad: { label: 'iPad', icon: 'tablet', viewportWidth: 810, viewportHeight: 1080, chromeType: 'tablet' },
  desktop: { label: 'Desktop (Laptop)', icon: 'monitor', viewportWidth: 1440, viewportHeight: 900, chromeType: 'desktop' },
  desktop_wide: { label: 'Desktop (Wide)', icon: 'monitor', viewportWidth: 1920, viewportHeight: 1080, chromeType: 'desktop' },
};

let frameCounter = 0;

export function createDeviceFrame(
  deviceType: DeviceType,
  x: number,
  y: number,
  overrides?: Partial<DeviceFrame>
): DeviceFrame {
  frameCounter++;
  const preset = deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[deviceType] : null;
  return {
    id: `frame-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: `Screen ${frameCounter}`,
    deviceType,
    x,
    y,
    viewportWidth: preset?.viewportWidth ?? 400,
    viewportHeight: preset?.viewportHeight ?? 600,
    scale: 1,
    backgroundColor: '#FFFFFF',
    children: [],
    scrollOffset: 0,
    locked: false,
    zIndex: 0,
    ...overrides,
  };
}

// ============================================================================
// Flow Arrows
// ============================================================================

export interface FlowArrow {
  id: string;
  sourceId: string;
  sourceAnchor: AnchorPosition;
  targetId: string;
  targetAnchor: AnchorPosition;
  label?: string;
  style: 'solid' | 'dashed';
  color: string;
  curveType: 'bezier' | 'orthogonal' | 'straight';
  waypoints?: { x: number; y: number }[];
}

export function createFlowArrow(
  sourceId: string,
  sourceAnchor: AnchorPosition,
  targetId: string,
  targetAnchor: AnchorPosition,
  overrides?: Partial<FlowArrow>
): FlowArrow {
  return {
    id: `arrow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    sourceId,
    sourceAnchor,
    targetId,
    targetAnchor,
    style: 'solid',
    color: 'var(--neutral-400)',
    curveType: 'bezier',
    ...overrides,
  };
}

// ============================================================================
// Whiteboard Elements
// ============================================================================

export interface WhiteboardElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  state: ElementState;
  props: ElementProps;
  zIndex: number;
  locked: boolean;
  visible: boolean;
  // Frame relationship
  parentFrameId?: string;
  relativeX?: number;
  relativeY?: number;
}

export interface ElementProps {
  // Button
  buttonVariant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  buttonLabel?: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  
  // Input
  inputType?: 'text' | 'email' | 'password' | 'number' | 'search' | 'tel' | 'url';
  placeholder?: string;
  inputLabel?: string;
  required?: boolean;
  helperText?: string;
  errorMessage?: string;
  
  // Dropdown
  dropdownOptions?: string[];
  dropdownPlaceholder?: string;
  dropdownMultiple?: boolean;
  dropdownSearchable?: boolean;
  
  // Checkbox / Toggle
  checked?: boolean;
  checkboxLabel?: string;
  
  // Radio Group
  radioOptions?: string[];
  radioSelected?: number;
  radioOrientation?: 'vertical' | 'horizontal';
  
  // Table
  tableColumns?: string[];
  tableRows?: number;
  tableHasHeader?: boolean;
  tableSortable?: boolean;
  tablePaginated?: boolean;
  
  // Text
  textContent?: string;
  textStyle?: 'h1' | 'h2' | 'h3' | 'h4' | 'body' | 'caption' | 'label';
  textAlign?: 'left' | 'center' | 'right';
  textColor?: string;
  
  // Image
  imageSrc?: string;
  imageAlt?: string;
  imageObjectFit?: 'cover' | 'contain' | 'fill';
  imageRadius?: number;
  
  // Shape
  shapeFill?: string;
  shapeStroke?: string;
  shapeStrokeWidth?: number;
  shapeCornerRadius?: number;
  shapeOpacity?: number;
  
  // Container / Card
  containerPadding?: number;
  containerBackground?: string;
  containerBorder?: boolean;
  containerBorderColor?: string;
  containerBorderRadius?: number;
  containerShadow?: boolean;
  containerShadowSize?: 'none' | 'sm' | 'md' | 'lg';
  
  // Tabs
  tabStyle?: 'underline' | 'pills' | 'boxed';
  
  // Accordion
  accordionAllowMultiple?: boolean;
  
  // List
  listDividers?: boolean;
  listShowIcon?: boolean;
  
  // Notes (BA annotations)
  notes?: string;
  tags?: string[];
}

export interface UserStory {
  asA: string;
  iWant: string;
  soThat: string;
}

export interface AcceptanceCriterion {
  id: string;
  text: string;
  priority: 'must' | 'should' | 'could' | 'wont';
  status: 'draft' | 'approved' | 'rejected';
  origin: 'manual' | 'ai';
}

// ============================================================================
// Whiteboard State
// ============================================================================

export interface WhiteboardState {
  elements: WhiteboardElement[];
  frames: DeviceFrame[];
  arrows: FlowArrow[];
  selectedIds: string[];
  selectedFrameId: string | null;
  activePanel: 'properties' | 'none';
  userStory: UserStory;
  acceptanceCriteria: AcceptanceCriterion[];
  zoom: number;
  pan: { x: number; y: number };
  showGrid: boolean;
  gridSize: number;
  snapToGrid: boolean;
  history: HistoryEntry[];
  historyIndex: number;
}

export interface HistoryEntry {
  elements: WhiteboardElement[];
  frames: DeviceFrame[];
  arrows: FlowArrow[];
}

// ============================================================================
// Smart Guides
// ============================================================================

export interface SnapGuide {
  type: 'horizontal' | 'vertical';
  position: number;
  start: number;
  end: number;
  label?: string;
}

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULT_ELEMENT_SIZES: Record<ElementType, { width: number; height: number }> = {
  button: { width: 120, height: 40 },
  input: { width: 240, height: 48 },
  dropdown: { width: 240, height: 48 },
  checkbox: { width: 160, height: 28 },
  radio_group: { width: 200, height: 100 },
  toggle: { width: 48, height: 24 },
  table: { width: 400, height: 240 },
  text: { width: 200, height: 32 },
  image: { width: 200, height: 150 },
  shape_rect: { width: 160, height: 100 },
  shape_circle: { width: 100, height: 100 },
  container: { width: 320, height: 240 },
  card: { width: 280, height: 180 },
  modal: { width: 480, height: 320 },
  tabs: { width: 400, height: 48 },
  accordion: { width: 300, height: 160 },
  list: { width: 280, height: 200 },
  icon: { width: 32, height: 32 },
};

export const DEFAULT_ELEMENT_LABELS: Record<ElementType, string> = {
  button: 'Button',
  input: 'Input Field',
  dropdown: 'Dropdown',
  checkbox: 'Checkbox',
  radio_group: 'Radio Group',
  toggle: 'Toggle',
  table: 'Data Table',
  text: 'Text Block',
  image: 'Image',
  shape_rect: 'Rectangle',
  shape_circle: 'Circle',
  container: 'Container',
  card: 'Card',
  modal: 'Modal',
  tabs: 'Tabs',
  accordion: 'Accordion',
  list: 'List',
  icon: 'Icon',
};

export function getDefaultProps(type: ElementType): ElementProps {
  switch (type) {
    case 'button':
      return { buttonVariant: 'primary', buttonLabel: 'Button', buttonSize: 'md' };
    case 'input':
      return { inputType: 'text', placeholder: 'Enter text...', inputLabel: 'Label', required: false };
    case 'dropdown':
      return { dropdownOptions: ['Option 1', 'Option 2', 'Option 3'], dropdownPlaceholder: 'Select...', dropdownMultiple: false, dropdownSearchable: false };
    case 'checkbox':
      return { checked: false, checkboxLabel: 'Checkbox option' };
    case 'radio_group':
      return { radioOptions: ['Option A', 'Option B', 'Option C'], radioSelected: 0, radioOrientation: 'vertical' };
    case 'toggle':
      return { checked: false, checkboxLabel: 'Toggle' };
    case 'table':
      return { tableColumns: ['Column 1', 'Column 2', 'Column 3'], tableRows: 5, tableHasHeader: true, tableSortable: true, tablePaginated: true };
    case 'text':
      return { textContent: 'Text content goes here', textStyle: 'body', textAlign: 'left' };
    case 'image':
      return { imageSrc: '', imageAlt: 'Image placeholder', imageObjectFit: 'cover', imageRadius: 8 };
    case 'shape_rect':
      return { shapeFill: '#E2E6F3', shapeStroke: '#C9D1EA', shapeStrokeWidth: 1, shapeCornerRadius: 8, shapeOpacity: 100 };
    case 'shape_circle':
      return { shapeFill: '#E2E6F3', shapeStroke: '#C9D1EA', shapeStrokeWidth: 1, shapeOpacity: 100 };
    case 'container':
      return { containerPadding: 16, containerBackground: 'transparent', containerBorder: true, containerShadow: false, containerShadowSize: 'none', containerBorderRadius: 8 };
    case 'card':
      return { containerPadding: 16, containerBackground: '#FFFFFF', containerBorder: true, containerShadow: true, containerShadowSize: 'md', containerBorderRadius: 12 };
    case 'modal':
      return { containerPadding: 24, containerBackground: '#FFFFFF', containerBorder: false, containerShadow: true, containerShadowSize: 'lg', containerBorderRadius: 12 };
    case 'tabs':
      return { dropdownOptions: ['Tab 1', 'Tab 2', 'Tab 3'], radioSelected: 0, tabStyle: 'underline' };
    case 'accordion':
      return { dropdownOptions: ['Section 1', 'Section 2', 'Section 3'], accordionAllowMultiple: false };
    case 'list':
      return { dropdownOptions: ['Item 1', 'Item 2', 'Item 3', 'Item 4'], listDividers: true, listShowIcon: false };
    case 'icon':
      return { textContent: '★' };
    default:
      return {};
  }
}

export function createWhiteboardElement(
  type: ElementType,
  x: number,
  y: number,
  overrides?: Partial<WhiteboardElement>
): WhiteboardElement {
  const size = DEFAULT_ELEMENT_SIZES[type];
  return {
    id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type,
    x,
    y,
    width: size.width,
    height: size.height,
    label: DEFAULT_ELEMENT_LABELS[type],
    state: 'default',
    props: getDefaultProps(type),
    zIndex: 1,
    locked: false,
    visible: true,
    ...overrides,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/** Get the absolute position of an element (accounting for frame parent) */
export function getElementAbsolutePosition(
  element: WhiteboardElement,
  frames: DeviceFrame[]
): { x: number; y: number } {
  if (element.parentFrameId) {
    const frame = frames.find(f => f.id === element.parentFrameId);
    if (frame) {
      // Chrome height: title bar (36px) + status bar for phones (44px for phone, 0 for others)
      const preset = frame.deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[frame.deviceType] : null;
      const chromeTop = 36 + (preset?.chromeType === 'phone' ? 44 : 0);
      return {
        x: frame.x + (element.relativeX ?? element.x),
        y: frame.y + chromeTop + (element.relativeY ?? element.y) - frame.scrollOffset,
      };
    }
  }
  return { x: element.x, y: element.y };
}

/** Get anchor point coordinates for an element or frame */
export function getAnchorPoint(
  anchor: AnchorPosition,
  rect: { x: number; y: number; width: number; height: number }
): { x: number; y: number } {
  switch (anchor) {
    case 'top':
      return { x: rect.x + rect.width / 2, y: rect.y };
    case 'right':
      return { x: rect.x + rect.width, y: rect.y + rect.height / 2 };
    case 'bottom':
      return { x: rect.x + rect.width / 2, y: rect.y + rect.height };
    case 'left':
      return { x: rect.x, y: rect.y + rect.height / 2 };
  }
}

/** Get the bounding rect for a frame (including chrome) */
export function getFrameBoundingRect(frame: DeviceFrame): { x: number; y: number; width: number; height: number } {
  const preset = frame.deviceType !== 'custom' ? DEVICE_FRAME_PRESETS[frame.deviceType] : null;
  const chromeTop = 36 + (preset?.chromeType === 'phone' ? 44 : 0);
  return {
    x: frame.x,
    y: frame.y,
    width: frame.viewportWidth,
    height: frame.viewportHeight + chromeTop,
  };
}
